import { useMemo, useState } from "react"
import {
	Badge,
	Box,
	Component,
	Divider,
	Heading,
	Icons,
	Inline,
	Select,
	Spinner,
	Stack,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	Text,
	Tooltip,
} from "@sembark-travel/ui-base"
import { formatMoneyByIntl, moneyParseByDecimal } from "@sembark-travel/money"
import { Dialog } from "@sembark-travel/ui-dialog"
import {
	ButtonLink,
	queryToSearch,
	useGetBackUrlFromLocation,
	useNavigateToBack,
} from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"
import { formatDate } from "@sembark-travel/datetime-utils"
import type { IHotel } from "../Hotels"
import type { THotelOtaRoomType } from "../HotelOtaRoomTypes"
import { generatePath } from "../router-utils"
import { HotelSidebarItem, PolicyChip, SeasonIntervals } from "./NewItem"
import type {
	INewRateSheetFormSchema,
	IRateBlockSchema,
	TGridRate,
} from "./NewItem"
import {
	columnPillVariant,
	GRID_BORDER_COLOR,
	NOT_SELLABLE_BACKGROUND,
	RATE_CELL_BACKGROUND,
	RATE_COL_MIN_WIDTH,
	ROOM_OCCUPANCY_COL_WIDTH,
} from "./gridStyles"
import {
	ColumnHeaderPill,
	MealPlanRowCell,
	MiniCurrency,
	OccupancyBadgeSlot,
	RoomInfoCell,
} from "./RateGridPrimitives"
import {
	rateSheetsXHR,
	TRateSheet,
	TRateSheetRateBlock,
	TRateSheetVersionsXHR,
	useIsMobile,
	useRateSheetHead,
	useSheetSelection,
} from "./store"
import {
	apiResponseToFormData,
	blockGridOverridesFromSavedRooms,
	buildSeasonDiffEntries,
	buildSheetRatesRef,
	computeSheetSummary,
	isRateSheetPolicyStale,
	lowerColsFromPaxTypes,
	// numberToHexColor,
	pluralize,
	policyHeadName,
	rateKey,
	roomColChanges,
	roomColsFromPaxTypes,
	TRoomColChanges,
	seasonDiffHasChanges,
	seasonDiffPolicyChanged,
	seasonDiffRatesChanged,
	TAnyPolicy,
	TSeasonDiffEntry,
	TSeasonMeta,
	TSheetSummary,
	TVersionDiffStatus,
	buildGridGeometry,
	buildGridRates,
	buildRateCellDiffs,
	collectRoomRateCellWarnings,
	getOccupancyInfo,
	getRoomBaseOccupancy,
	TGridGeometry,
	TLowerCol,
	TRateCellChangeStatus,
	TRateCellDiff,
	TRoomCol,
} from "./utils"

export function RateSheetDetail({ id }: { id: number }) {
	const { data: head, isLoading, error } = useRateSheetHead(id)

	if (isLoading) {
		return (
			<Box padding="6" textAlign="center">
				<Spinner />
			</Box>
		)
	}

	if (error || !head) {
		return (
			<Box padding="6" textAlign="center">
				<Text color="danger">Failed to load the rate sheet.</Text>
			</Box>
		)
	}

	return (
		<Stack>
			<RateSheetDetailHeader
				id={id}
				name={head.name}
				rateSheet={head.latest_rate_sheet}
			/>
			<RateSheetView rateSheet={head.latest_rate_sheet} />
		</Stack>
	)
}

// One version of a sheet, read-only. The detail page renders the head's latest
// version with it, and the shared (token) view the version that was shared.
export function RateSheetView({ rateSheet }: { rateSheet: TRateSheet }) {
	const sheet = useMemo(() => apiResponseToFormData(rateSheet), [rateSheet])

	// The form values flatten a block's room types and pax types down to ids, and
	// the grid needs the objects — so the blocks are indexed straight off the
	// response, by the same season x hotel key the rest of the sheet uses.
	const apiBlocksByKey = useMemo(() => {
		const byKey = new Map<string, TRateSheetRateBlock>()
		rateSheet.seasons.forEach((season, seasonIdx) => {
			season.blocks.forEach((block) => {
				byKey.set(rateKey(seasonIdx, block.hotel.id), block)
			})
		})
		return byKey
	}, [rateSheet])

	return <RateSheetDetailBody sheet={sheet} apiBlocksByKey={apiBlocksByKey} />
}

function RateSheetDetailHeader({
	id,
	name,
	rateSheet,
}: {
	id: number
	name: string
	rateSheet: TRateSheet
}) {
	const navigateToBack = useNavigateToBack("/org/rate-sheets")
	const back = useGetBackUrlFromLocation()
	const isMobile = useIsMobile()

	return (
		<Inline
			gap={isMobile ? "2" : "4"}
			justifyContent="between"
			alignItems="center"
			style={{ backgroundColor: "#F9F9FB" }}
			paddingY="3"
			paddingX={isMobile ? "3" : "6"}
			borderBottomWidth="1"
			flexWrap="wrap"
		>
			<Inline gap="3" alignItems="center" minWidth="0">
				<Box
					cursor="pointer"
					display="flex"
					alignItems="center"
					onClick={() => navigateToBack()}
					title="Back to Rate Sheets"
				>
					<Icons.ArrowLeft size="5" />
				</Box>
				<Heading as="h3" textOverflow="truncate" title={name}>
					{name}
				</Heading>
				<Badge primary borderWidth="0">
					code: {id}
				</Badge>
				<VersionHistoryChip rateSheet={rateSheet} api={rateSheetsXHR} />
			</Inline>
			<Inline gap="4" alignItems="center" flexShrink="0">
				<ButtonLink
					level="primary"
					replace
					to={generatePath("/rate-sheets/:rateSheetId/edit", {
						rateSheetId: String(id),
					})}
					search={back ? queryToSearch({ back }) : ""}
				>
					Edit Rate Sheet
				</ButtonLink>
			</Inline>
		</Inline>
	)
}

function RateSheetDetailBody({
	sheet,
	apiBlocksByKey,
}: {
	sheet: INewRateSheetFormSchema
	apiBlocksByKey: Map<string, TRateSheetRateBlock>
}) {
	const isMobile = useIsMobile()
	const hotels = sheet.hotels || []
	const seasons = sheet.seasons || []
	const {
		activeHotelId,
		activeSeasonIdx,
		setSelectedHotelId,
		setSelectedSeasonIdx,
	} = useSheetSelection(hotels, seasons)

	const activeSeason = seasons[activeSeasonIdx]
	const activeBlock = activeSeason?.rate_blocks?.find(
		(block) => Number(block.hotel) === activeHotelId
	)
	const activeApiBlock = apiBlocksByKey.get(
		rateKey(activeSeasonIdx, activeHotelId)
	)
	const activeHotel = hotels.find((hotel) => hotel.id === activeHotelId)

	if (hotels.length === 0 || seasons.length === 0) {
		return (
			<Stack
				alignItems="center"
				justifyContent="center"
				gap="2"
				paddingY="16"
				paddingX="6"
			>
				<Icons.Bed size="12" color="subtle" />
				<Text color="muted">
					{hotels.length === 0
						? "No hotels selected."
						: "No seasons configured."}
				</Text>
			</Stack>
		)
	}

	return (
		<Tabs layout={isMobile ? "col" : "row"}>
			<TabsList
				position={isMobile ? undefined : "sticky"}
				style={{
					minWidth: isMobile ? undefined : "12rem",
					maxWidth: isMobile ? undefined : "16rem",
					padding: 0,
					rowGap: 0,
				}}
				flexShrink="0"
				minHeight="full"
			>
				{isMobile ? null : (
					<>
						<Inline
							alignItems="center"
							padding="4"
							paddingLeft="6"
							bgColor="white"
						>
							<Text
								fontWeight="semibold"
								color="muted"
								style={{ lineHeight: "28px" }}
							>
								{`Seasons (${seasons.length})`} <Icons.ArrowLeft rotate="180" />
							</Text>
						</Inline>
						<Inline
							paddingLeft="6"
							alignItems="center"
							color="muted"
							gap="1"
							fontWeight="semibold"
							bgColor="white"
							padding="4"
						>
							<Text
								style={{ lineHeight: "28px" }}
							>{`Hotels (${hotels.length})`}</Text>
							<Box>
								<Icons.ArrowLeft rotate="270" />
							</Box>
						</Inline>
					</>
				)}
				{hotels.map((hotel) => {
					const hotelId = Number(hotel.id)
					return (
						<HotelSidebarItem
							key={hotel.id}
							hotel={hotel}
							active={activeHotelId === hotelId}
							onClick={() => setSelectedHotelId(hotelId)}
						/>
					)
				})}
			</TabsList>

			<TabContent active flex="1" minWidth="0" style={{ padding: 0 }}>
				<Stack>
					<Tabs layout="col">
						<TabsList
							flex="1"
							borderBottomWidth={isMobile ? "1" : "0"}
							style={{ paddingInline: 0 }}
						>
							{seasons.map((season, sIdx) => (
								<TabItem
									key={sIdx}
									active={activeSeasonIdx === sIdx}
									onClick={() => setSelectedSeasonIdx(sIdx)}
									bgColor={activeSeasonIdx === sIdx ? "primary" : undefined}
									style={{
										borderBottom: isMobile ? 0 : undefined,
									}}
								>
									<Stack paddingX={isMobile ? "1" : "2"}>
										<Text
											fontWeight="semibold"
											color={activeSeasonIdx === sIdx ? "primary" : undefined}
										>
											{season.name}
										</Text>
										<Text fontSize="xs" color="muted" fontWeight="normal">
											{pluralize(season.intervals.length, "Interval")}
										</Text>
									</Stack>
								</TabItem>
							))}
						</TabsList>
					</Tabs>

					{activeSeason?.intervals?.length ? (
						<SeasonIntervals intervals={activeSeason.intervals} />
					) : null}

					<RateSheetDetailBlock
						hotel={activeHotel}
						block={activeBlock}
						apiBlock={activeApiBlock}
						isMobile={isMobile}
					/>
				</Stack>
			</TabContent>
		</Tabs>
	)
}

// Everything priced for one hotel in one season: the policies it sells under
// and the rates themselves.
function RateSheetDetailBlock({
	hotel,
	block,
	apiBlock,
	isMobile,
}: {
	hotel: IHotel | undefined
	block: IRateBlockSchema | undefined
	apiBlock: TRateSheetRateBlock | undefined
	isMobile: boolean
}) {
	const paxTypes = apiBlock?.pax_types
	// The room's occupancy is read off the block's own saved room record, not
	// the (possibly since-changed) hotel OTA room type
	const roomTypes = useMemo(
		() =>
			(apiBlock?.rooms || []).map(
				(room): THotelOtaRoomType => ({
					id: room.ota_room_type.id,
					name: room.ota_room_type.name,
					includes_all_meal_plans: room.ota_room_type.includes_all_meal_plans,
					no_of_rooms: room.ota_room_type.no_of_rooms,
					order: room.ota_room_type.order,
					ota_meal_plans: [],
					pax_types: [],
					pax_types_with_lower_occupancies_configuration: [],
					base_adult_occupancy: room.base_adult_occupancy,
					base_child_occupancy: room.base_child_occupancy,
					max_occupancy: room.max_occupancy,
					max_adult_occupancy: room.max_adult_occupancy,
					max_child_occupancy: room.max_child_occupancy,
				})
			),
		[apiBlock]
	)
	const roomCols = useMemo(() => roomColsFromPaxTypes(paxTypes), [paxTypes])
	const lowerCols = useMemo(() => lowerColsFromPaxTypes(paxTypes), [paxTypes])
	if (!block || !apiBlock) {
		return (
			<Stack padding={isMobile ? "3" : "6"}>
				<Text color="muted">Nothing priced for this hotel in this season.</Text>
			</Stack>
		)
	}

	// The block holds the policies it sells under — its hotel's, unless it
	// overrode them — and each names its head and the head's latest version
	const { cancellation_policy, payment_policy } = apiBlock

	return (
		<Stack gap="6" padding={isMobile ? "3" : "6"}>
			<Inline gap="6" alignItems="center" flexWrap="wrap">
				<Heading as="h4" color="primary">
					Policies
				</Heading>
				<PolicyChip
					kind="cancellation"
					policy={cancellation_policy}
					isStale={isRateSheetPolicyStale(cancellation_policy)}
				/>
				<PolicyChip
					kind="payment"
					policy={payment_policy}
					isStale={isRateSheetPolicyStale(payment_policy)}
				/>
			</Inline>

			<Stack gap="6">
				<Heading as="h4" color="primary">
					Rates
				</Heading>
				<RoomRatesPreviewTable
					// The block's own hotel comes back thin — no `ota_meal_plans` — and a
					// meal plan sold on only some of the rooms records that nowhere but
					// the hotel's OTA configuration, which the sheet's hotel entry carries.
					// Without it the grid would show every plan on every room.
					hotel={hotel || apiBlock.hotel}
					rates={block.rates}
					mealPlans={block.meal_plans}
					roomTypes={roomTypes}
					roomCols={roomCols}
					lowerCols={lowerCols}
					configureLowerOccupancy={apiBlock.is_lower_occupancy_rate_configured}
					currency={apiBlock.hotel.currency}
				/>
			</Stack>
		</Stack>
	)
}

// The version badge of a sheet's header, opening its version history once
// there is an older version to compare with
export function VersionHistoryChip({
	rateSheet,
	api,
}: {
	rateSheet: TRateSheet
	api: TRateSheetVersionsXHR
}) {
	const hasMultipleVersions = rateSheet.version > 1

	return (
		<VersionHistoryDialog currentRateSheet={rateSheet} api={api}>
			{({ open }) => (
				<Box
					bgColor="inset"
					padding="1"
					rounded="md"
					cursor={hasMultipleVersions ? "pointer" : "default"}
					onClick={hasMultipleVersions ? open : undefined}
					title={hasMultipleVersions ? "Version History" : "First version"}
				>
					<Text fontSize="sm" color="muted" fontWeight="semibold">
						v{rateSheet.version}
					</Text>
				</Box>
			)}
		</VersionHistoryDialog>
	)
}

// `api` decides where the versions are read from: the signed in endpoints, or
// the shared (token) ones, which only list up to the version that was shared.
export function VersionHistoryDialog({
	currentRateSheet,
	api,
	children,
}: {
	currentRateSheet: TRateSheet
	api: TRateSheetVersionsXHR
	children: (props: { open: () => void }) => React.ReactNode
}) {
	return (
		<Component initialState={false}>
			{({ state, setState }) => (
				<>
					{children({ open: () => setState(true) })}
					<Dialog
						open={state}
						onClose={() => setState(false)}
						title="Version History"
						xl
					>
						<VersionHistory currentRateSheet={currentRateSheet} api={api} />
					</Dialog>
				</>
			)}
		</Component>
	)
}

function VersionHistory({
	currentRateSheet,
	api,
}: {
	currentRateSheet: TRateSheet
	api: TRateSheetVersionsXHR
}) {
	const isMobile = useIsMobile()
	const xhr = useXHR()
	const headId = currentRateSheet.rate_sheet_head_id

	// `api` is part of the keys because the two sources share these ids but not
	// what they return
	const { data: versions, isLoading: isLoadingVersions } = useSWR(
		["rate-sheet-versions", headId, api],
		() => api(xhr).getVersions(headId)
	)

	const currentSheet = useMemo(
		() => apiResponseToFormData(currentRateSheet),
		[currentRateSheet]
	)

	const sortedVersions = useMemo(
		() =>
			versions?.data
				? [...versions.data].sort((a, b) => b.version - a.version)
				: [],
		[versions]
	)

	const currentVersion = sortedVersions.find(
		(v) => v.id === currentRateSheet.id
	)

	const [selectedVersionId, setSelectedVersionId] = useState<number | null>(
		null
	)

	// Default to the newest version other than the current one; once the user
	// picks one, use that.
	const activeVersionId =
		selectedVersionId ||
		sortedVersions.find((v) => v.id !== currentRateSheet.id)?.id ||
		null

	const { data: previousVersion, isLoading: isLoadingPrevious } = useSWR(
		activeVersionId ? ["rate-sheet-version", activeVersionId, api] : null,
		() => api(xhr).getVersionById(Number(activeVersionId))
	)

	// The selected version read into the same form values the current one is
	// held in, which is what lets the two be diffed by the wizard's own helpers.
	const previousSheet = useMemo(
		() => (previousVersion ? apiResponseToFormData(previousVersion) : null),
		[previousVersion]
	)

	const selectedVersion = sortedVersions.find((v) => v.id === activeVersionId)

	if (isLoadingVersions) {
		return (
			<Box padding="4" textAlign="center">
				<Spinner />
			</Box>
		)
	}

	if (!sortedVersions.length) {
		return <Text color="muted">No versions yet.</Text>
	}

	if (sortedVersions.length < 2) {
		return (
			<Stack
				padding="6"
				gap="4"
				justifyContent="center"
				alignItems="center"
				textAlign="center"
			>
				<Icons.Info size="16" color="subtle" />
				<Text color="muted">Only one version exists. Nothing to compare.</Text>
			</Stack>
		)
	}

	if (isMobile) {
		const selectableVersions = sortedVersions
			.filter((v) => v.id !== currentRateSheet.id)
			.map((v) => {
				return { id: v.id, name: `v${v.version}`, created_at: v.created_at }
			})
		const selectedOption = selectableVersions.find(
			(o) => o.id === activeVersionId
		)

		return (
			<Stack gap="4" padding="4" style={{ minHeight: "60vh" }}>
				<Stack gap="3">
					<Inline gap="2" alignItems="center">
						<Select
							value={selectedOption}
							onChange={(option: (typeof selectableVersions)[number]) => {
								if (option) setSelectedVersionId(option.id)
							}}
							clearable={false}
							options={selectableVersions}
							optionRenderer={({ option }) => {
								return (
									<Stack>
										<Text>{option.name}</Text>
										<Text fontSize="sm">
											{formatDate(option.created_at, "DD MMM, YYYY")}
										</Text>
									</Stack>
								)
							}}
							style={{ maxWidth: "9rem" }}
						/>
						<Text color="muted">→</Text>
						<Text fontWeight="semibold">v{currentVersion?.version}</Text>
						<Badge accent>Current</Badge>
					</Inline>
					<DiffLegend />
				</Stack>
				<Divider marginY="0" />

				{isLoadingPrevious ? (
					<Box padding="8" textAlign="center">
						<Spinner />
					</Box>
				) : previousSheet && currentVersion && selectedVersion ? (
					<DiffBody latest={currentSheet} previous={previousSheet} />
				) : (
					<Text color="muted">Select a version to compare.</Text>
				)}
			</Stack>
		)
	}

	return (
		<Tabs layout="row" collapseBelowXs={false} style={{ minHeight: "60vh" }}>
			{/* Left sidebar — version list */}
			<TabsList
				style={{
					minWidth: "12rem",
					maxWidth: "14rem",
					padding: 0,
					rowGap: 0,
				}}
				roundedBottomLeft="lg"
				flexShrink="0"
			>
				<Box padding="6" paddingBottom="2">
					<Text fontWeight="semibold" color="muted">
						Versions
					</Text>
				</Box>
				{sortedVersions.map((version) => {
					const isCurrent = version.id === currentRateSheet.id
					const isActive = version.id === activeVersionId
					return (
						<TabItem
							key={version.id}
							active={isActive}
							onClick={
								isCurrent ? undefined : () => setSelectedVersionId(version.id)
							}
							bgColor={isActive ? "primary" : undefined}
							style={{
								padding: 0,
								cursor: isCurrent ? "not-allowed" : "pointer",
							}}
						>
							<Stack gap="1">
								<Inline gap="2" alignItems="center">
									<Text
										fontWeight="semibold"
										color={isActive ? "primary" : undefined}
									>
										v{version.version}
									</Text>
									{isCurrent ? <Badge accent>Current</Badge> : null}
								</Inline>
								<Text fontSize="xs" color="muted" fontWeight="normal">
									{formatDate(version.created_at, "DD MMM YYYY, HH:mm")}
								</Text>
							</Stack>
						</TabItem>
					)
				})}
			</TabsList>

			{/* Right panel — diff content */}
			<TabContent active flex="1" minWidth="0" overflow="auto" padding="4">
				{isLoadingPrevious ? (
					<Box padding="8" textAlign="center">
						<Spinner />
					</Box>
				) : previousSheet && currentVersion && selectedVersion ? (
					<Stack gap="6">
						<Inline
							justifyContent="between"
							alignItems="center"
							flexWrap="wrap"
							gap="3"
						>
							<Inline gap="2" alignItems="center">
								<Text fontWeight="semibold">v{selectedVersion.version}</Text>
								<Text color="muted">→</Text>
								<Text fontWeight="semibold">v{currentVersion.version}</Text>
								<Badge accent>Current</Badge>
							</Inline>
							<DiffLegend />
						</Inline>

						<DiffBody latest={currentSheet} previous={previousSheet} />
					</Stack>
				) : (
					<Text color="muted">Select a version to compare.</Text>
				)}
			</TabContent>
		</Tabs>
	)
}

// Static legend indicators at the top right of the diff view.
function DiffLegend() {
	return (
		<Inline gap="2" alignItems="center" flexWrap="wrap">
			<LegendPill label="Price ↓" backgroundColor="#B9F8CF" color="#2A7E3B" />
			<LegendPill label="Price ↑" backgroundColor="#FFC9C9" color="#D13415" />
			<LegendPill label="Added" backgroundColor="#EDF2FE" color="#3E63DD" />
			<LegendPill label="Removed" backgroundColor="#E0E1E6" color="#60646C" />
		</Inline>
	)
}

function LegendPill({
	label,
	backgroundColor,
	color,
}: {
	label: string
	backgroundColor: string
	color: string
}) {
	return (
		<Box
			display="inlineBlock"
			paddingX="3"
			paddingY="1"
			rounded="md"
			style={{ backgroundColor, color }}
		>
			<Text
				fontSize="sm"
				fontWeight="semibold"
				textDecoration={label === "Removed" ? "lineThrough" : undefined}
			>
				{label}
			</Text>
		</Box>
	)
}

function toSeasonsMeta(
	seasons: INewRateSheetFormSchema["seasons"] | undefined
): TSeasonMeta[] {
	return (seasons || []).map(({ _uid, name, intervals }) => ({
		_uid,
		name,
		intervals,
	}))
}

// Renders the full diff body: hotels, then the seasons under each. Both are
// unions of the two versions, so a hotel or season that only exists on one side
// still gets a row.
function DiffBody({
	latest,
	previous,
}: {
	latest: INewRateSheetFormSchema
	previous: INewRateSheetFormSchema
}) {
	const latestHotels = useMemo(() => latest.hotels || [], [latest])
	// A saved sheet's hotels already carry their OTA room types and meal plans,
	// which is all the grids are built from — so nothing is fetched per hotel,
	// and the dialog works in the shared (token) view, which cannot read hotels.
	const previousHotels = useMemo(() => previous.hotels || [], [previous])

	const currentRatesRef = useMemo(
		() => buildSheetRatesRef(latest.seasons),
		[latest]
	)
	const previousRatesRef = useMemo(
		() => buildSheetRatesRef(previous.seasons),
		[previous]
	)

	const summary = useMemo(
		() =>
			computeSheetSummary({
				hotels: latestHotels,
				seasons: toSeasonsMeta(latest.seasons),
				hotelPolicies: latest.hotelPolicies || {},
				hotelPaymentPolicies: latest.hotelPaymentPolicies || {},
				ratesRef: currentRatesRef,
				originalRatesRef: previousRatesRef,
				originalSeasons: toSeasonsMeta(previous.seasons),
				originalHotels: previousHotels,
				originalHotelPolicies: previous.hotelPolicies || {},
				originalHotelPaymentPolicies: previous.hotelPaymentPolicies || {},
				isEdit: true,
			}),
		[
			latest,
			previous,
			latestHotels,
			previousHotels,
			currentRatesRef,
			previousRatesRef,
		]
	)

	// Hotels in the latest version first, in its own order, then the ones only
	// the old version had.
	const hotelEntries = useMemo(() => {
		const latestIds = new Set(latestHotels.map((h) => Number(h.id)))
		const previousIds = new Set(previousHotels.map((h) => Number(h.id)))
		return [
			...latestHotels.map((hotel) => ({
				hotel,
				status: (previousIds.has(Number(hotel.id))
					? "updated"
					: "added") as TVersionDiffStatus,
			})),
			...previousHotels
				.filter((hotel) => !latestIds.has(Number(hotel.id)))
				.map((hotel) => ({ hotel, status: "removed" as TVersionDiffStatus })),
		]
	}, [latestHotels, previousHotels])

	// Every section prunes itself, so two identical versions would otherwise
	// leave the panel blank. The same reading step 3 puts on an edit with nothing
	// to save.
	if (summary.changes.length === 0) {
		return <Text color="muted">No changes between these versions.</Text>
	}

	return (
		<Stack gap="6">
			{hotelEntries.map(({ hotel, status }) => (
				<HotelDiffSection
					key={hotel.id}
					hotel={hotel}
					hotelStatus={status}
					latest={latest}
					previous={previous}
					currentRatesRef={currentRatesRef}
					previousRatesRef={previousRatesRef}
					summary={summary}
				/>
			))}
		</Stack>
	)
}

function HotelDiffSection({
	hotel,
	hotelStatus,
	latest,
	previous,
	currentRatesRef,
	previousRatesRef,
	summary,
}: {
	hotel: IHotel
	hotelStatus: TVersionDiffStatus
	latest: INewRateSheetFormSchema
	previous: INewRateSheetFormSchema
	currentRatesRef: Map<string, IRateBlockSchema>
	previousRatesRef: Map<string, IRateBlockSchema>
	summary: TSheetSummary
}) {
	const hotelId = Number(hotel.id)

	const entries = useMemo(
		() =>
			buildSeasonDiffEntries({
				hotelId,
				hotelStatus,
				seasons: toSeasonsMeta(latest.seasons),
				baselineSeasons: toSeasonsMeta(previous.seasons),
				ratesRef: currentRatesRef,
				baselineRatesRef: previousRatesRef,
			}),
		[latest, previous, hotelId, hotelStatus, currentRatesRef, previousRatesRef]
	)

	const visibleEntries = entries.filter((entry) =>
		seasonDiffHasChanges(
			entry,
			hotel,
			entry.status === "updated"
				? summary.byBlock
						.get(rateKey(entry.seasonIdx, hotelId))
						?.changes.map((change) => change.message)
				: undefined
		)
	)

	if (hotelStatus === "updated" && visibleEntries.length === 0) return null

	const isAdded = hotelStatus === "added"
	const isRemoved = hotelStatus === "removed"
	const borderColor = isAdded ? "#2B8A3E" : isRemoved ? "#868E96" : "#3A5BC7"

	return (
		<Stack gap="4">
			<Box
				style={{
					borderLeft: `3px solid ${borderColor}`,
					paddingLeft: "0.75rem",
				}}
			>
				<Inline gap="2" alignItems="center">
					<Heading
						as="h4"
						color={isRemoved ? "muted" : "primary"}
						style={isRemoved ? { textDecoration: "line-through" } : undefined}
					>
						{hotel.name}
					</Heading>
					{isAdded ? (
						<Badge status="accent">Added</Badge>
					) : isRemoved ? (
						<Badge>Removed</Badge>
					) : null}
				</Inline>
			</Box>

			<Stack gap="4">
				{visibleEntries.map((entry) => (
					<SeasonDiffSection key={entry.key} entry={entry} hotel={hotel} />
				))}
			</Stack>
		</Stack>
	)
}

function SeasonDiffSection({
	entry,
	hotel,
}: {
	entry: TSeasonDiffEntry
	hotel: IHotel
}) {
	const { status, name } = entry
	const isAdded = status === "added"
	const isRemoved = status === "removed"

	const showRateTable = seasonDiffRatesChanged(entry, hotel)
	const showPolicy = seasonDiffPolicyChanged(entry)

	return (
		<Stack gap="3">
			<Inline gap="2" alignItems="center" paddingLeft="2">
				<Text
					fontWeight="semibold"
					style={isRemoved ? { textDecoration: "line-through" } : undefined}
					color="muted"
				>
					{name}
				</Text>
				{isAdded ? (
					<Badge status="accent">Added</Badge>
				) : isRemoved ? (
					<Badge>Removed</Badge>
				) : (
					<Badge status="warning">Updated</Badge>
				)}
			</Inline>

			{entry.seasonChanges.length ? (
				<Stack gap="1" paddingLeft="2">
					{entry.seasonChanges.map((message) => (
						<Text key={message} fontSize="sm" color="muted">
							{message}
						</Text>
					))}
				</Stack>
			) : null}

			{showRateTable ? <DiffRateTable entry={entry} hotel={hotel} /> : null}

			{showPolicy ? <SeasonPolicyDiff entry={entry} /> : null}
		</Stack>
	)
}

// The policies a season sells under. An added or removed season has only one
// side to show; an updated one shows the move, with the version on each side
// because a sheet pins a version rather than a policy.
function SeasonPolicyDiff({ entry }: { entry: TSeasonDiffEntry }) {
	return (
		<Stack gap="3">
			<OnePolicyDiff
				label="Cancellation Policy"
				status={entry.status}
				current={entry.block?.cancellation_policy}
				previous={entry.baselineBlock?.cancellation_policy}
			/>
			<OnePolicyDiff
				label="Payment Policy"
				status={entry.status}
				current={entry.block?.payment_policy}
				previous={entry.baselineBlock?.payment_policy}
			/>
		</Stack>
	)
}

function OnePolicyDiff({
	label,
	status,
	current,
	previous,
}: {
	label: string
	status: TSeasonDiffEntry["status"]
	current: TAnyPolicy | null | undefined
	previous: TAnyPolicy | null | undefined
}) {
	if (status === "removed") {
		if (!previous) return null
		return (
			<PolicyBlock label={label}>
				<Text color="muted" style={{ textDecoration: "line-through" }}>
					<PolicyName policy={previous} />
				</Text>
			</PolicyBlock>
		)
	}

	if (status === "added") {
		if (!current) return null
		return (
			<PolicyBlock label={label}>
				<Text fontWeight="semibold">
					<PolicyName policy={current} />
				</Text>
			</PolicyBlock>
		)
	}

	if ((current?.id ?? null) === (previous?.id ?? null)) return null

	return (
		<PolicyBlock label={label}>
			<Inline gap="2" alignItems="center" flexWrap="wrap">
				<Text color="muted" style={{ textDecoration: "line-through" }}>
					<PolicyName policy={previous} />
				</Text>
				<Text color="muted">→</Text>
				<Text fontWeight="semibold">
					<PolicyName policy={current} />
				</Text>
			</Inline>
		</PolicyBlock>
	)
}

function PolicyName({ policy }: { policy: TAnyPolicy | null | undefined }) {
	if (!policy) return <>None</>
	return (
		<>
			{policyHeadName(policy) || "None"}
			{policy.version ? ` v${policy.version}` : ""}
		</>
	)
}

function PolicyBlock({
	label,
	children,
}: {
	label: string
	children: React.ReactNode
}) {
	return (
		<Stack gap="1" paddingLeft="2">
			<Text fontSize="sm" color="muted">
				{label}
			</Text>
			{children}
		</Stack>
	)
}

// The rate grid of one hotel x season. A season that only one version has is
// drawn from that version's block against an empty other side, so every priced
// cell reads as added or removed — the same comparison the wizard's preview
// draws, just with nothing on one side of it.
function DiffRateTable({
	entry,
	hotel,
}: {
	entry: TSeasonDiffEntry
	hotel: IHotel
}) {
	const isRemoved = entry.status === "removed"
	const isAdded = entry.status === "added"

	// A removed season has no current block, so its geometry — and its meal plan
	// rows — comes off the baseline. Its whole table already reads as removed, so
	// the lower occupancy columns are not singled out on top of that.
	const geometryBlock = isRemoved ? entry.baselineBlock : entry.block
	const configureLowerOccupancy = Boolean(
		geometryBlock?.is_lower_occupancy_rate_configured
	)
	const initialConfigureLowerOccupancy = isRemoved
		? configureLowerOccupancy
		: Boolean(entry.baselineBlock?.is_lower_occupancy_rate_configured)

	const { roomTypes, roomCols, lowerCols } = useMemo(
		() =>
			blockGridOverridesFromSavedRooms(
				hotel,
				geometryBlock,
				isRemoved ? undefined : entry.baselineBlock
			),
		[hotel, geometryBlock, entry.baselineBlock, isRemoved]
	)

	// An empty baseline still builds a full grid of blanks, which is what makes
	// every priced cell of an added season read as added.
	const initialRates = isAdded ? [] : entry.baselineBlock?.rates

	if (!geometryBlock) return null

	return (
		<Box rounded="md">
			<RoomRatesPreviewTable
				hotel={hotel}
				rates={isRemoved ? undefined : entry.block?.rates}
				initialRates={initialRates}
				mealPlans={geometryBlock.meal_plans}
				roomTypes={roomTypes}
				roomCols={roomCols}
				lowerCols={lowerCols}
				colChanges={roomColChanges(
					isRemoved ? undefined : entry.block,
					entry.baselineBlock
				)}
				configureLowerOccupancy={configureLowerOccupancy}
				initialConfigureLowerOccupancy={initialConfigureLowerOccupancy}
				isAddedBlock={isAdded}
				isEdit
				currency={hotel.currency}
				showWarnings={false}
				title={null}
			/>
		</Box>
	)
}

// How a changed cell is painted. Direction is the thing worth seeing at a
// glance, so a rate that went up and one that came down never share a colour.
// A cell that gained or lost a rate outright has no direction to report, and
// takes the neutral accent instead.
const CELL_STATUS_STYLE: Record<
	TRateCellChangeStatus,
	{
		bgColor?: "accent"
		backgroundColor?: string
		color: "danger" | "success" | "muted" | "primary"
		fontWeight?: "semibold"
	}
> = {
	increased: {
		backgroundColor: "#FFC9C9",
		color: "danger",
		fontWeight: "semibold",
	},
	decreased: {
		backgroundColor: "#B9F8CF",
		color: "success",
		fontWeight: "semibold",
	},
	added: {
		backgroundColor: "#EDF2FE",
		color: "primary",
		fontWeight: "semibold",
	},
	removed: {
		backgroundColor: "#E0E1E6",
		color: "muted",
		fontWeight: "semibold",
	},
}

// The edge a wholly gained block is drawn with — the same accent the diff
// legend gives "Added". A wholly removed one is left alone: its cells are
// already greyed and struck through, which reads as a loss on its own.
const ADDED_BLOCK_BORDER_COLOR = "#3E63DD"

function formatRate(value: number | null | undefined, currency?: string) {
	if (value == null) return "-"
	// An absent currency falls through to the configured default inside
	// `moneyParseByDecimal`, which is what a block on a hotel with no currency
	// of its own wants.
	return formatMoneyByIntl(moneyParseByDecimal(value, currency || ""))
}

export type TRoomRatesPreviewTableProps = {
	// Needs the detailed record — `ota_room_types` and `ota_meal_plans` are what
	// the grid's geometry is derived from. A hotel from the list endpoint
	// carries neither and renders an empty table.
	hotel: IHotel
	rates: IRateBlockSchema["rates"] | undefined
	// The rates this block was loaded with. Comparison is off without it.
	initialRates?: IRateBlockSchema["rates"] | null
	mealPlans?: IRateBlockSchema["meal_plans"]
	// A block may price a subset of the hotel's rooms
	roomTypes?: THotelOtaRoomType[]
	// The grid's columns. Derived from the hotel's own pax type configuration
	// when absent, which is what the wizard wants — a saved sheet passes the
	// columns it was priced with instead, read off its pax types.
	roomCols?: TRoomCol[]
	lowerCols?: TLowerCol[]
	// The extra pax columns the block gained or lost against its baseline, so a
	// re-cut child age range reads as one column added and one removed. A
	// removed column only renders when `roomCols` was widened to hold it.
	colChanges?: TRoomColChanges
	configureLowerOccupancy?: boolean
	// Whether the block being compared against priced lower occupancies. The
	// switch is per block, so it can differ from `configureLowerOccupancy` — and
	// when it does, that whole group of columns was gained or lost and is painted
	// as such rather than quietly appearing or disappearing.
	initialConfigureLowerOccupancy?: boolean
	// Set when the block as a whole is new, rather than anything inside it having
	// moved. Every cell is tinted identically in that case, which reads as a
	// colour scheme rather than as a change, so the table says it on its own edge.
	isAddedBlock?: boolean
	// Gates the comparison chrome, so a create flow stays plain even if a
	// baseline happens to be passed
	isEdit?: boolean
	currency?: string
	showWarnings?: boolean
	title?: React.ReactNode
	emptyMessage?: React.ReactNode
}

export function RoomRatesPreviewTable({
	hotel,
	rates,
	initialRates,
	mealPlans,
	roomTypes,
	roomCols: roomColsOverride,
	lowerCols: lowerColsOverride,
	colChanges,
	configureLowerOccupancy = false,
	initialConfigureLowerOccupancy,
	isAddedBlock = false,
	isEdit = false,
	currency,
	showWarnings = true,
	title = "Room Rates",
	emptyMessage = "No room rates configured",
}: TRoomRatesPreviewTableProps) {
	const resolvedCurrency = currency || hotel.currency
	const comparing = isEdit && initialRates != null

	// The lower occupancy columns have to be laid out whenever either side
	// priced them, or a block that switched them off would simply lose those
	// columns and the rates under them without ever showing that it had.
	const showLowerOccupancy =
		configureLowerOccupancy ||
		(comparing && Boolean(initialConfigureLowerOccupancy))

	// They move as a group, because the block holds one switch for all of them.
	const lowerColsStatus: TRateCellChangeStatus | null = !comparing
		? null
		: configureLowerOccupancy && !initialConfigureLowerOccupancy
			? "added"
			: !configureLowerOccupancy && initialConfigureLowerOccupancy
				? "removed"
				: null

	const roomColStatusByKey = useMemo(() => {
		const statuses = new Map<string, "added" | "removed">()
		if (!comparing) return statuses
		colChanges?.added.forEach((col) => statuses.set(col.key, "added"))
		colChanges?.removed.forEach((col) => statuses.set(col.key, "removed"))
		return statuses
	}, [comparing, colChanges])

	const geometry = useMemo(
		() =>
			buildGridGeometry(hotel, mealPlans, showLowerOccupancy, {
				roomTypes,
				roomCols: roomColsOverride,
				lowerCols: lowerColsOverride,
			}),
		[
			hotel,
			mealPlans,
			showLowerOccupancy,
			roomTypes,
			roomColsOverride,
			lowerColsOverride,
		]
	)

	// Both grids come off the same geometry, which is what makes them index
	// aligned — the diff below addresses cells positionally.
	const grid = useMemo(
		() =>
			buildGridRates(
				geometry.visibleRows,
				geometry.lowerCols,
				geometry.roomCols,
				showLowerOccupancy,
				rates
			),
		[geometry, showLowerOccupancy, rates]
	)

	const diffs = useMemo(() => {
		if (!isEdit || !initialRates) return new Map<number, TRateCellDiff>()
		const baselineGrid = buildGridRates(
			geometry.visibleRows,
			geometry.lowerCols,
			geometry.roomCols,
			showLowerOccupancy,
			initialRates
		)
		return buildRateCellDiffs(grid, baselineGrid, true)
	}, [isEdit, initialRates, geometry, showLowerOccupancy, grid])

	const warnings = useMemo(() => {
		if (!showWarnings) return new Map<number, string>()
		return collectRoomRateCellWarnings(
			geometry,
			showLowerOccupancy,
			(cellIdx) => {
				const value = grid[cellIdx]?.rate_before_tax
				return value == null ? null : Number(value)
			}
		)
	}, [showWarnings, geometry, showLowerOccupancy, grid])

	const { activeRoomTypes, baseMealPlan, lowerCols, roomCols, visibleRows } =
		geometry

	if (!visibleRows.length || !activeRoomTypes.length) {
		return (
			<Stack gap="3">
				<PreviewSectionTitle title={title} currency={resolvedCurrency} />
				<Text color="muted">{emptyMessage}</Text>
			</Stack>
		)
	}

	return (
		<Stack gap="3">
			<PreviewSectionTitle
				title={title}
				currency={resolvedCurrency}
				suffix="(before taxes)"
			/>
			<Box overflow="auto" rounded="md">
				<Box
					as="table"
					style={{
						borderCollapse: "separate",
						borderSpacing: 0,
						width: "auto",
						borderColor: isAddedBlock ? ADDED_BLOCK_BORDER_COLOR : undefined,
					}}
					borderColor={isAddedBlock ? undefined : "emphasis"}
					borderWidth={isAddedBlock ? "2" : "1"}
					rounded="lg"
					overflow="hidden"
					bgColor="white"
				>
					<Box as="thead">
						<Box as="tr" bgColor="white">
							<Box
								as="th"
								padding="3"
								fontWeight="semibold"
								textAlign="left"
								bgColor="subtle"
								color="muted"
								borderRightWidth="1"
								borderBottomWidth="1"
								style={{
									borderColor: GRID_BORDER_COLOR,
									width: `calc(${ROOM_OCCUPANCY_COL_WIDTH} - 50px)`,
									maxWidth: ROOM_OCCUPANCY_COL_WIDTH,
									position: "sticky",
									left: 0,
									zIndex: 2,
									backgroundColor: "#F8FAFC",
								}}
							>
								<Inline gap="4" flexWrap="wrapReverse">
									<Text whiteSpace="nowrap">
										Room Type <Icons.ArrowLeft rotate="270" />
									</Text>
									<Text whiteSpace="nowrap">
										Occupancy <Icons.ArrowLeft rotate="180" />
									</Text>
								</Inline>
							</Box>
							{showLowerOccupancy
								? lowerCols.map((col) => (
										<HeaderCell
											key={col.key}
											backgroundColor={
												lowerColsStatus
													? CELL_STATUS_STYLE[lowerColsStatus].backgroundColor
													: undefined
											}
										>
											<ColumnHeaderPill
												label={col.label}
												variant="lower"
												paddingX="3"
												diffStatus={lowerColsStatus}
											/>
										</HeaderCell>
									))
								: null}
							<HeaderCell>
								<ColumnHeaderPill label="BASE" variant="base" />
							</HeaderCell>
							{roomCols.map((col) => {
								const colStatus = roomColStatusByKey.get(col.key)
								return (
									<HeaderCell
										key={col.key}
										backgroundColor={
											colStatus
												? CELL_STATUS_STYLE[colStatus].backgroundColor
												: undefined
										}
									>
										<ColumnHeaderPill
											label={col.label || col.key.toUpperCase()}
											variant={columnPillVariant(col)}
											diffStatus={colStatus}
										/>
									</HeaderCell>
								)
							})}
						</Box>
					</Box>

					{/* One row group per room type — its header row and the meal plan
					    sub rows priced under it */}
					{activeRoomTypes.map((rt) => {
						const baseOccupancy = getRoomBaseOccupancy(rt)
						const rows = visibleRows
							.map((row, rowIndex) => ({ ...row, rowIndex }))
							.filter((row) => row.rt.id === rt.id)
						const baseRow = rows.find((row) => row.isBase)
						const subRows = rows.filter((row) => !row.isBase)

						const baseOffset = showLowerOccupancy
							? geometry.lowerCols.length
							: 0
						const baseCellDiff =
							baseRow === undefined
								? undefined
								: diffs.get(
										baseRow.rowIndex * geometry.itemsPerRoom + baseOffset
									)
						const isRoomAdded = baseCellDiff?.status === "added"
						const isRoomRemoved = baseCellDiff?.status === "removed"
						const roomDiffStatus = isRoomAdded
							? "added"
							: isRoomRemoved
								? "removed"
								: null

						return (
							<Box as="tbody" key={rt.id}>
								<Box as="tr" bgColor={isRoomAdded ? undefined : "white"}>
									<RoomInfoCell
										roomType={rt}
										baseOccupancy={baseOccupancy}
										baseMealPlan={baseMealPlan}
										diffStatus={roomDiffStatus}
									/>
									{baseRow ? (
										<RateRowCells
											rowIndex={baseRow.rowIndex}
											baseOccupancy={baseOccupancy}
											geometry={geometry}
											configureLowerOccupancy={showLowerOccupancy}
											lowerColsStatus={lowerColsStatus}
											grid={grid}
											diffs={diffs}
											warnings={warnings}
											currency={resolvedCurrency}
											showOccupancy
										/>
									) : (
										<Box
											as="td"
											colSpan={geometry.itemsPerRoom}
											padding="3"
											textAlign="center"
											borderBottomWidth="1"
											style={{ borderColor: GRID_BORDER_COLOR }}
										>
											<Text color="muted" fontSize="sm">
												No base meal plan priced
											</Text>
										</Box>
									)}
								</Box>

								{subRows.map((row) => {
									const subRowStart = row.rowIndex * geometry.itemsPerRoom
									const isSubRowAdded =
										isRoomAdded ||
										rowIsEntirely("added", subRowStart, geometry, grid, diffs)
									const isSubRowRemoved =
										isRoomRemoved ||
										rowIsEntirely("removed", subRowStart, geometry, grid, diffs)
									const subRowDiffStatus = isSubRowAdded
										? "added"
										: isSubRowRemoved
											? "removed"
											: null

									return (
										<Box
											as="tr"
											key={`${rt.id}_${row.mp.id}`}
											bgColor={
												isSubRowAdded || isSubRowRemoved ? undefined : "white"
											}
										>
											<MealPlanRowCell
												mealPlan={row.mp}
												diffStatus={subRowDiffStatus}
											/>
											<RateRowCells
												rowIndex={row.rowIndex}
												baseOccupancy={baseOccupancy}
												geometry={geometry}
												configureLowerOccupancy={showLowerOccupancy}
												lowerColsStatus={lowerColsStatus}
												grid={grid}
												diffs={diffs}
												warnings={warnings}
												currency={resolvedCurrency}
												backgroundColor={
													isSubRowRemoved
														? "#E0E1E6"
														: isSubRowAdded
															? "#EDF2FE"
															: RATE_CELL_BACKGROUND
												}
											/>
										</Box>
									)
								})}
							</Box>
						)
					})}
				</Box>
			</Box>
		</Stack>
	)
}

// Whether every cell of a row that has anything to say says the same thing —
// what marks a whole meal plan row as gained or lost rather than tinting its
// cells one by one.
//
// A cell holding no rate and reporting no diff is not evidence either way: the
// occupancies a room cannot be sold at are placeholders the diff never visits,
// and a cell that was blank before and is blank now has nothing to contribute.
// Counting those as dissent is what kept a fully removed row from ever reading
// as one. A cell still holding an unchanged rate is dissent, and does count.
function rowIsEntirely(
	status: TRateCellChangeStatus,
	rowStart: number,
	geometry: TGridGeometry,
	grid: TGridRate[],
	diffs: Map<number, TRateCellDiff>
): boolean {
	let sawStatus = false
	for (let i = 0; i < geometry.itemsPerRoom; i++) {
		const cellIdx = rowStart + i
		const diff = diffs.get(cellIdx)
		if (diff) {
			if (diff.status !== status) return false
			sawStatus = true
			continue
		}
		// No diff — only a blank cell abstains
		if (grid[cellIdx]?.rate_before_tax != null) return false
	}
	return sawStatus
}

function PreviewSectionTitle({
	title,
	currency,
	suffix,
}: {
	title: React.ReactNode
	currency?: string
	suffix?: string
}) {
	return (
		<Inline gap="1" paddingLeft="4">
			<Text fontWeight="semibold">{title}</Text>
			{currency ? <MiniCurrency currency={currency} /> : null}
			{suffix ? (
				<Text color="muted" fontSize="sm">
					{suffix}
				</Text>
			) : null}
		</Inline>
	)
}

function HeaderCell({
	children,
	backgroundColor,
}: {
	children: React.ReactNode
	backgroundColor?: string
}) {
	return (
		<Box
			as="th"
			paddingX="2"
			paddingY="2"
			textAlign="center"
			verticalAlign="top"
			borderRightWidth="1"
			borderBottomWidth="1"
			style={{
				borderColor: GRID_BORDER_COLOR,
				minWidth: RATE_COL_MIN_WIDTH,
				backgroundColor,
			}}
		>
			{children}
		</Box>
	)
}

// The rate cells of one row, in the grid's column order: the lower occupancies
// the room can be sold at, then BASE, then the extra pax columns.
function RateRowCells({
	rowIndex,
	baseOccupancy,
	geometry,
	configureLowerOccupancy,
	lowerColsStatus,
	grid,
	diffs,
	warnings,
	currency,
	showOccupancy,
	backgroundColor,
}: {
	rowIndex: number
	baseOccupancy: number
	geometry: TGridGeometry
	configureLowerOccupancy: boolean
	// Set when the whole lower occupancy group was gained or lost, which paints
	// those columns as one rather than leaving it to the cells to agree
	lowerColsStatus?: TRateCellChangeStatus | null
	grid: TGridRate[]
	diffs: Map<number, TRateCellDiff>
	warnings: Map<number, string>
	currency?: string
	showOccupancy?: boolean
	backgroundColor?: string
}) {
	const { lowerCols, roomCols, itemsPerRoom } = geometry
	const rowStart = rowIndex * itemsPerRoom
	const baseOffset = configureLowerOccupancy ? lowerCols.length : 0

	// Only the BASE cell of a room's header row is labelled, but every cell of
	// that row reserves the slot so the values stay on one line.
	const occupancySlot = showOccupancy ? <OccupancyBadgeSlot /> : null

	return (
		<>
			{configureLowerOccupancy
				? lowerCols.map((col, colIdx) => {
						if (col.occ >= baseOccupancy) {
							return (
								<Box
									as="td"
									key={col.key}
									padding="2"
									textAlign="center"
									borderRightWidth="1"
									borderBottomWidth="1"
									style={{
										borderColor: GRID_BORDER_COLOR,
										background: NOT_SELLABLE_BACKGROUND,
									}}
								/>
							)
						}
						return (
							<RateCell
								key={col.key}
								cellIdx={rowStart + colIdx}
								grid={grid}
								diffs={diffs}
								forcedStatus={lowerColsStatus}
								warnings={warnings}
								currency={currency}
								occupancySlot={occupancySlot}
								backgroundColor={backgroundColor}
							/>
						)
					})
				: null}

			<RateCell
				cellIdx={rowStart + baseOffset}
				grid={grid}
				diffs={diffs}
				warnings={warnings}
				currency={currency}
				occupancySlot={
					showOccupancy ? (
						<OccupancyBadgeSlot label={getOccupancyInfo(baseOccupancy).label} />
					) : null
				}
				backgroundColor={backgroundColor}
			/>

			{roomCols.map((col, colIdx) => (
				<RateCell
					key={col.key}
					cellIdx={rowStart + baseOffset + 1 + colIdx}
					grid={grid}
					diffs={diffs}
					warnings={warnings}
					currency={currency}
					occupancySlot={occupancySlot}
					backgroundColor={backgroundColor}
				/>
			))}
		</>
	)
}

function RateCell({
	cellIdx,
	grid,
	diffs,
	forcedStatus,
	warnings,
	currency,
	occupancySlot,
	backgroundColor,
}: {
	cellIdx: number
	grid: TGridRate[]
	diffs: Map<number, TRateCellDiff>
	// Overrides the cell's own reading. A column the block gained or lost is
	// painted whole, including the cells that were never priced on either side
	// and so have no difference of their own to report.
	forcedStatus?: TRateCellChangeStatus | null
	warnings: Map<number, string>
	currency?: string
	occupancySlot?: React.ReactNode
	backgroundColor?: string
}) {
	const diff = diffs.get(cellIdx)
	const warning = warnings.get(cellIdx)
	const value = grid[cellIdx]?.rate_before_tax
	const cellStatus = forcedStatus || diff?.status || null
	const status = cellStatus ? CELL_STATUS_STYLE[cellStatus] : null
	const isRemoved = cellStatus === "removed"

	return (
		<Box
			as="td"
			padding="2"
			paddingX="4"
			textAlign="center"
			verticalAlign="top"
			borderRightWidth="1"
			borderBottomWidth="1"
			bgColor={status ? status.bgColor : undefined}
			style={{
				borderColor: GRID_BORDER_COLOR,
				backgroundColor: status ? status.backgroundColor : backgroundColor,
			}}
		>
			<Stack gap="1">
				{occupancySlot}
				<Inline gap="1" justifyContent="center" alignItems="center">
					{!isRemoved ? (
						<Text
							color={status ? status.color : undefined}
							fontWeight={status ? status.fontWeight : undefined}
						>
							{formatRate(value == null ? null : Number(value), currency)}
						</Text>
					) : null}
					{warning ? (
						<Tooltip content={<Text>{warning}</Text>}>
							<Text>
								<Icons.Attention color="warning_light" size="4" />
							</Text>
						</Tooltip>
					) : null}
				</Inline>
				{diff && diff.initial != null ? (
					<Text
						fontSize="sm"
						color="muted"
						style={{ textDecoration: "line-through" }}
					>
						{formatRate(diff.initial, currency)}
					</Text>
				) : null}
			</Stack>
		</Box>
	)
}
